import { Check, Close } from "@mui/icons-material";
import { Box, Button, Typography } from "@mui/material";
import { ErrorMessage, FormikValues, useFormikContext } from "formik";
import { FC, useEffect, useState } from "react";
import { toast } from "react-toastify";
import fileUploadService from "../../../../services/fileUpload";

import IMAGES from "../../../../constants/images";
import styles from "./FileUploadButton.module.scss";
import { logger } from "../../../../utils/logger";
import {
  createFileName,
  getFileName,
  getTimeStamp,
} from "../../../../utils/helper";
import { _URL } from "../../../../constants/forms";
import useAppStore from "../../../../states/zustand/appStore";

interface IProps {
  name: string;
  label?: string;
  fileName?: string;
  className?: string;
  index?: number;
  gridName?: string;
  protocolNumber?: string;
  docName?: string;
  showError?: boolean;
  [x: string]: any;
}

const FileUploadButton: FC<IProps> = (props) => {
  const {
    name,
    label = "Upload",
    fileName = "File",
    className,
    index = 0,
    gridName,
    protocolNumber,
    docName,
    showError = true,
    ...rest
  } = props;
  const { setLoader } = useAppStore();

  const {
    values,
    setFieldValue,
    validateForm,
    errors,
    setFieldTouched,
    setFieldError,
    setErrors,
  } = useFormikContext<FormikValues>();

  const [uploadedUrl, setUploadedUrl] = useState(values[name]);

  const getFileExtension = (link: string) => {
    const re = /(?:\.([^.]+))?$/;
    return re.exec(link)?.[1];
  };

  const checkFormValidation = (modifiedValues: FormikValues) => {
    const prevErrors = { ...errors };
    validateForm(modifiedValues).then((res) => {
      if (res[name]) {
        setFieldError(name, JSON.stringify(res[name]));
      }
    });
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (!e.target.files) return;

    try {
      // dispatch(setLoadingAction(true));
      setLoader(true);
      const imageUrl = await fileUploadService.upload(
        e.target.files,
        protocolNumber
          ? createFileName(protocolNumber, docName || name)
          : `${getTimeStamp()}-${docName || name}`
      );
      setLoader(false);
      setUploadedUrl(imageUrl.data);
      // dispatch(setLoadingAction(false));

      if (gridName) {
        const prev = [...values[name]];
        prev[index][gridName] = imageUrl.data;
        setFieldValue(name, prev);
        checkFormValidation({ ...values, [name]: imageUrl.data });

        return;
      }

      setFieldValue(name, imageUrl.data);
      toast.success("File uploaded successfully");
      // checkFormValidation({ ...values, [name]: imageUrl.data });
    } catch (err: any) {
      setLoader(false);
      console.log({ err });
      toast.error("Cannot upload a file");
      // dispatch(setLoadingAction(false));
    }
  };

  const handleFileDelete = async () => {
    setLoader(true);
    await fileUploadService.deleteFile(uploadedUrl !== ""? uploadedUrl: values[name]);
    setLoader(false);
    if (gridName) {
      const prev = [...values[name]];
      prev[index][gridName] = "";
      setFieldValue(name, prev);
      checkFormValidation({ ...values, [name]: prev });
      return;
    } else {
      setFieldValue(name, "");
      checkFormValidation({ ...values, [name]: "" });
    }
  };

  const isShowUploadButton = (): boolean => {
    if (gridName) {
      return !values[name][index][gridName];
    } else {
      return !values[name];
    }
  };

  useEffect(() => {
    setFieldTouched(name, false);
  }, []);

  return (
    <>
      <Box
        className={`d-flex align-items-center ${
          className ? className : "mg-t-16"
        }`}
      >
        {isShowUploadButton() && (
          <Box className="upload-box d-flex align-items-center justify-content-center mg-r-16">
            <label>
              <i className="icon-upload-cloud mg-r-10"></i>
              <Typography
                className="mg-r-6 font-size-sm font-weight-medium"
                variant={"subtitle1"}
                component={"span"}
              >
                {label}
              </Typography>
            </label>
            <div className="hidden-input">
              <input
                type="file"
                accept="application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.ms-powerpoint, application/vnd.openxmlformats-officedocument.presentationml.presentation"
                // accept="image/png, image/gif, image/jpeg, application/pdf"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleFileUpload(e)
                }
                className="form-control"
                name={name}
                // onClick={(e) => { e.preventDefault(); }}
                {...rest}
              />
            </div>
          </Box>
        )}

        {!isShowUploadButton() && (
          <div className={styles.uploaded}>
            <div className="d-flex gap-xs">
              <img src={IMAGES.Clipboard} alt="" />{" "}
              {/* <span>{uploadedFileName}</span> */}
              <span className="ellipsis">
                {typeof values[name] === "string"
                  ? getFileName(values[name])
                  : getFileName(values[name][index][_URL])}
              </span>
            </div>
            <div className="d-flex justify-content-center align-items-center">
              <Check sx={{ color: "green" }} />
              <Button type="button" onClick={handleFileDelete}>
                <Close
                  sx={{
                    color: "#9FA2B4",
                  }}
                />
              </Button>
            </div>
          </div>
        )}
      </Box>
      {showError && (
        <Box>
          <ErrorMessage
            name={name}
            render={(msg) => (
              <span className="text-danger font-size-sm">{msg}</span>
            )}
          />
        </Box>
      )}
    </>
  );
};

export default FileUploadButton;
